import math from '../../helpers/math'

interface ICoordinates {
  x: number
  y: number
}
export interface IRectangleSidesCoordinates {
  topLeft: ICoordinates
  topRight: ICoordinates
  rightTop: ICoordinates
  rightBottom: ICoordinates
  bottomRight: ICoordinates
  bottomLeft: ICoordinates
  leftBottom: ICoordinates
  leftTop: ICoordinates
}

export interface ITailCoordinates {
  tailRoot: ICoordinates
  tailTip: ICoordinates
}

export enum TailDirection {
  noTail,
  topLeftCorner,
  topRightCorner,
  bottomLeftCorner,
  bottomRightCorner,
  topSide,
  rightSide,
  bottomSide,
  leftSide,
}

export const getRectangleSidesCoordinates = ({
  padding,
  x2,
  y2,
  textWidth,
  measuredHeight,
}: {
  padding: number
  x2: number
  y2: number
  textWidth: number
  measuredHeight: number
}): IRectangleSidesCoordinates => ({
  topLeft: {
    x: x2 - padding,
    y: y2 - padding,
  },
  topRight: {
    x: x2 + textWidth + padding,
    y: y2 - padding,
  },
  rightTop: {
    x: x2 + textWidth + padding,
    y: y2 - padding,
  },
  rightBottom: {
    x: x2 + textWidth + padding,
    y: y2 + padding + measuredHeight,
  },
  bottomRight: {
    x: x2 + textWidth + padding,
    y: y2 + padding + measuredHeight,
  },
  bottomLeft: {
    x: x2 - padding,
    y: y2 + padding + measuredHeight,
  },
  leftBottom: {
    x: x2 - padding,
    y: y2 + padding + measuredHeight,
  },
  leftTop: {
    x: x2 - padding,
    y: y2 - padding,
  },
})

export const getTailCoordinates = ({
  rectangleSides,
  x1,
  y1,
}: {
  rectangleSides: IRectangleSidesCoordinates
  x1: number
  y1: number
}): ITailCoordinates => ({
  tailRoot: {
    x: rectangleSides.topLeft.x + (rectangleSides.topRight.x - rectangleSides.topLeft.x) / 2,
    y: rectangleSides.leftTop.y + (rectangleSides.leftBottom.y - rectangleSides.leftTop.y) / 2,
  },
  tailTip: {
    x: x1,
    y: y1,
  },
})

export const getTailToSideIntersection = ({
  tail,
  rectangleSides,
}: {
  tail: ITailCoordinates
  rectangleSides: IRectangleSidesCoordinates
}): TailDirection => {
  const { topLeft, topRight, rightTop, rightBottom, bottomRight, bottomLeft, leftBottom, leftTop } = rectangleSides
  const { tailTip, tailRoot } = tail
  if (math.twoSegmentLinesIntersection(tailTip, tailRoot, topLeft, leftTop)) {
    return TailDirection.topLeftCorner
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, topRight, rightTop)) {
    return TailDirection.topRightCorner
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, bottomLeft, leftBottom)) {
    return TailDirection.bottomLeftCorner
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, bottomRight, rightBottom)) {
    return TailDirection.bottomRightCorner
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, topLeft, topRight)) {
    return TailDirection.topSide
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, rightTop, rightBottom)) {
    return TailDirection.rightSide
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, bottomLeft, bottomRight)) {
    return TailDirection.bottomSide
  } else if (math.twoSegmentLinesIntersection(tailTip, tailRoot, leftTop, leftBottom)) {
    return TailDirection.leftSide
  } else {
    return TailDirection.noTail
  }
}

export const getTailEdgeIntersection = (
  tail: ITailCoordinates,
  rectangleSides: IRectangleSidesCoordinates,
  tailDirection: TailDirection
) => {
  const { topLeft, topRight, bottomLeft, bottomRight } = rectangleSides
  const { tailRoot, tailTip } = tail

  // Find intersection with rectangle edges based on tail direction
  switch (tailDirection) {
    case TailDirection.topSide:
    case TailDirection.topLeftCorner:
    case TailDirection.topRightCorner:
      return math.twoSegmentLinesIntersection(tailRoot, tailTip, topLeft, topRight)
    case TailDirection.rightSide:
      return math.twoSegmentLinesIntersection(tailRoot, tailTip, topRight, bottomRight)
    case TailDirection.bottomSide:
    case TailDirection.bottomLeftCorner:
    case TailDirection.bottomRightCorner:
      return math.twoSegmentLinesIntersection(tailRoot, tailTip, bottomLeft, bottomRight)
    case TailDirection.leftSide:
      return math.twoSegmentLinesIntersection(tailRoot, tailTip, topLeft, bottomLeft)
    default:
      return tailRoot
  }
}
